import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "editorContainer",
    "queryInput",
    "hiddenQuery",
    "form",
    "runButton",
    "statusText",
    "charCount",
    "resultsTable",
  ]

  static values = {
    objectsUrl: String,
    allFieldsUrl: String,
    shareBaseUrl: String,
    initialQuery: { type: String, default: "" },
  }

  connect() {
    this.editorView = null
    this.fieldCache = {}
    this.fieldLoadTimer = null

    // Seed the hidden input immediately so form submissions work
    // even before CodeMirror finishes loading
    if (this.hasHiddenQueryTarget && this.initialQueryValue) {
      this.hiddenQueryTarget.value = this.initialQueryValue
    }

    this.initCodeMirror()

    // Keyboard shortcut on textarea fallback
    if (this.hasQueryInputTarget) {
      this.queryInputTarget.addEventListener("keydown", (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
          e.preventDefault()
          this.submitQuery()
        }
      })
      this.queryInputTarget.addEventListener("input", () => {
        this.updateCharCount(this.queryInputTarget.value.length)
      })
    }

    // Sync CodeMirror value into hidden input before Turbo captures form data.
    // Must use native "submit" event (fires before Turbo serializes FormData).
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", () => {
        this.syncQueryToHiddenInput()
      })
      this.formTarget.addEventListener("turbo:submit-start", () => {
        this.setRunning(true)
      })
      this.formTarget.addEventListener("turbo:submit-end", () => {
        this.setRunning(false)
      })
    }
  }

  disconnect() {
    if (this.fieldLoadTimer) {
      clearTimeout(this.fieldLoadTimer)
      this.fieldLoadTimer = null
    }
    if (this.editorView) {
      this.editorView.destroy()
      this.editorView = null
    }
  }

  async initCodeMirror() {
    try {
      const { createSoqlEditor } = await import("../lib/soql/editor")

      if (this.hasQueryInputTarget) {
        this.queryInputTarget.style.display = "none"
      }

      this.editorView = createSoqlEditor({
        parent: this.editorContainerTarget,
        schema: {},
        initialDoc: this.initialQueryValue,
        onRun: () => this.submitQuery(),
        onChange: (query) => {
          this.updateCharCount(query.length)
          this.detectAndLoadFields(query)
        },
      })

      if (this.initialQueryValue) {
        this.updateCharCount(this.initialQueryValue.length)
      }

      this.fetchObjectsForSchema().then((schema) => {
        this.currentSchema = schema
        if (this.editorView && this.editorView.updateSchema) {
          this.editorView.updateSchema(schema)
        }
      })
    } catch (e) {
      console.warn("CodeMirror failed to load, using textarea fallback:", e)
    }
  }

  syncQueryToHiddenInput() {
    if (this.hasHiddenQueryTarget) {
      this.hiddenQueryTarget.value = this.getQuery()
    }
  }

  submitQuery() {
    const query = this.getQuery().trim()
    if (!query) return

    this.syncQueryToHiddenInput()
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  async fetchObjectsForSchema() {
    try {
      const response = await fetch(this.objectsUrlValue, {
        headers: { Accept: "application/json" },
      })
      const data = await response.json()

      if (data.objects) {
        const schema = {}
        data.objects.forEach((obj) => {
          schema[obj.name] = []
        })
        return schema
      }
    } catch (e) {
      console.warn("Failed to fetch Salesforce objects for autocomplete:", e)
    }
    return {}
  }

  detectAndLoadFields(query) {
    // Debounce: autocomplete fires onChange for every keystroke, and a FROM
    // clause is a partial object name until typing settles. Wait for a pause
    // before hitting Salesforce so we describe "Account" once, not "A"/"Ac"/...
    if (this.fieldLoadTimer) clearTimeout(this.fieldLoadTimer)
    this.fieldLoadTimer = setTimeout(() => this.loadFieldsForQuery(query), 300)
  }

  async loadFieldsForQuery(query) {
    const fromMatch = query.match(/\bFROM\s+(\w+)/i)
    if (!fromMatch) return

    const objectName = fromMatch[1]
    if (this.fieldCache[objectName]) return

    // Only describe objects that exist in the org. The eligible list is the full
    // catalog (from the all_objects endpoint), so every real object the editor
    // can query passes — this filters out mid-typing partials like "Ac" without
    // constraining which objects autocomplete, so no doomed round-trip goes out.
    if (!this.isKnownObject(objectName)) return

    try {
      const url = `${this.allFieldsUrlValue}?object_name=${encodeURIComponent(objectName)}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      })
      const data = await response.json()

      if (data.fields) {
        this.fieldCache[objectName] = data.fields.map((f) => f.name)

        if (this.editorView && this.editorView.updateSchema) {
          const updatedSchema = { ...this.currentSchema }
          updatedSchema[objectName] = this.fieldCache[objectName]
          this.currentSchema = updatedSchema
          this.editorView.updateSchema(updatedSchema)
        }
      }
    } catch (e) {
      console.warn(`Failed to fetch fields for ${objectName}:`, e)
    }
  }

  isKnownObject(objectName) {
    if (!this.currentSchema) return false
    // SOQL object names are case-insensitive; the schema keys come from
    // Salesforce's canonical casing, so compare case-folded.
    const target = objectName.toLowerCase()
    return Object.keys(this.currentSchema).some((name) => name.toLowerCase() === target)
  }

  getQuery() {
    if (this.editorView) {
      return this.editorView.state.doc.toString()
    }
    return this.hasQueryInputTarget ? this.queryInputTarget.value : ""
  }

  updateCharCount(count) {
    if (this.hasCharCountTarget) {
      this.charCountTarget.textContent = `${count} chars`
    }
  }

  setRunning(running) {
    if (this.hasRunButtonTarget) {
      this.runButtonTarget.disabled = running
      this.runButtonTarget.textContent = running ? "Running..." : "Run Query"
    }
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = running ? "Executing query..." : ""
    }
  }

  copyShareLink(event) {
    const queryId = event.currentTarget.dataset.queryId
    if (!queryId || !this.shareBaseUrlValue) return

    const url = `${window.location.origin}${this.shareBaseUrlValue}?query_id=${queryId}`
    navigator.clipboard.writeText(url).then(() => {
      if (this.hasStatusTextTarget) {
        this.statusTextTarget.textContent = "Share link copied!"
        setTimeout(() => {
          this.statusTextTarget.textContent = ""
        }, 2000)
      }
    })
  }

  downloadCsv() {
    if (!this.hasResultsTableTarget) return

    const table = this.resultsTableTarget
    const rows = Array.from(table.querySelectorAll("tr"))
    const lines = rows.map((row) => {
      const cells = Array.from(row.querySelectorAll("th, td"))
      return cells
        .map((cell) => {
          // Blank cells are rendered as italic "blank" — export as empty
          if (cell.classList.contains("italic") && cell.textContent.trim() === "blank") {
            return ""
          }
          const text = cell.getAttribute("title") || cell.textContent.trim()
          if (text.includes(",") || text.includes('"') || text.includes("\n")) {
            return `"${text.replace(/"/g, '""')}"`
          }
          return text
        })
        .join(",")
    })

    const blob = new Blob([lines.join("\n")], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "soql_results.csv"
    a.click()
    URL.revokeObjectURL(url)
  }
}
